import React, { useState } from 'react';

import Button from '../../../components/ui/Button';

const ParticipantManagementPanel = ({
  participants,
  sessionId,
  currentUserId = '1',
  canStartSplitting = false,
  onInviteParticipant,
  onRemoveParticipant,
  onStartSplitting
}) => {
  const [showInviteOptions, setShowInviteOptions] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Prefer explicit production base URL (set at build time via VITE_BASE_URL),
  // otherwise use the runtime origin. This prevents localhost in generated links
  // when the app is running in development or from packaged assets.
  const envBase = import.meta.env.VITE_BASE_URL || '';
  const runtimeOrigin = (window?.location?.origin) || '';
  const baseOrigin = (runtimeOrigin.includes('localhost') || !runtimeOrigin)
    ? (envBase || 'https://splitbill.logicmindstudio.com')
    : runtimeOrigin.replace(/\/$/, '');
  const sessionUrl = `${baseOrigin}/join/${sessionId}`;
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(sessionUrl)}`;

  // Debug logging in dev to confirm that generated links use production origin
  if (import.meta.env.DEV) {
    console.debug(`[ParticipantInvite] baseOrigin=${baseOrigin} sessionUrl=${sessionUrl}`);
  }

  const getInitials = (name) => {
    return name?.split(' ')?.map(n => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected':
        return 'bg-success';
      case 'pending':
        return 'bg-warning';
      case 'disconnected':
        return 'bg-error';
      default:
        return 'bg-muted';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'connected':
        return 'Conectado';
      case 'pending':
        return 'Pendiente';
      case 'disconnected':
        return 'Desconectado';
      default:
        return 'Desconocido';
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard?.writeText(sessionUrl);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleShareWhatsApp = () => {
    const message = encodeURIComponent(`¡Únete a nuestra cuenta compartida! ${sessionUrl}`);
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const connectedParticipants = participants?.filter(p => p?.status === 'connected');
  const currentUser = participants?.find(p => String(p?.id) === String(currentUserId));
  const isCurrentUserHost = currentUser?.isHost === true;

  // Note: canStartSplitting is now passed as prop from parent component
  // It checks if user is host AND has selected items

  return (
    <div className="bg-surface border border-border rounded-lg overflow-hidden">
      {/* Header */}
      <div className="p-4 bg-muted border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Comensales</h3>
            <p className="text-sm text-text-secondary">
              {connectedParticipants?.length} de {participants?.length} conectados
            </p>
          </div>
          <div className="flex items-center gap-2">
            {currentUser && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  // allow current user to leave the session
                  if (onRemoveParticipant) onRemoveParticipant(currentUser.id);
                }}
                iconName="LogOut"
                iconSize={14}
                className="text-text-secondary hover:text-foreground"
                title="Salir de la sesión"
              >
                Salir
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowInviteOptions(!showInviteOptions)}
              iconName="UserPlus"
              iconSize={16}
            >
              Invitar
            </Button>
          </div>
        </div>
      </div>
      {/* Invite Options */}
      {showInviteOptions && (
        <div className="p-4 bg-primary/5 border-b border-border">
          <div className="grid md:grid-cols-2 gap-4">
            {/* QR Code */}
            <div className="text-center">
              <div className="inline-block p-3 bg-white rounded-lg border border-border mb-2">
                <img
                  src={qrCodeUrl}
                  alt="Código QR para unirse a la sesión de división de cuenta"
                  className="w-20 h-20"
                  onError={(e) => {
                    e.target.src = '/assets/images/no_image.png';
                  }}
                />
              </div>
              <p className="text-xs text-text-secondary">
                Escanea para unirte
              </p>
            </div>

            {/* Share Options */}
            <div className="space-y-2">
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={sessionUrl}
                  readOnly
                  className="flex-1 px-3 py-2 text-xs border border-border rounded bg-input text-text-secondary"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyLink}
                  iconName={copySuccess ? "Check" : "Copy"}
                  iconSize={14}
                  className={copySuccess ? "text-success border-success" : ""}
                >
                  {copySuccess ? "✓" : ""}
                </Button>
              </div>

              <Button
                variant="outline"
                size="sm"
                fullWidth
                onClick={handleShareWhatsApp}
                iconName="MessageCircle"
                iconPosition="left"
                iconSize={14}
              >
                WhatsApp
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Participants List */}
      <div className="max-h-64 overflow-y-auto">
        <div className="divide-y divide-border">
          {participants?.map((participant) => (
            <div key={participant?.id} className="p-4 hover:bg-muted/30 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="relative">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium">
                      {getInitials(participant?.name)}
                    </div>
                    <div
                      className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-surface ${getStatusColor(participant?.status)}`}
                    />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <h4 className="text-sm font-medium text-foreground truncate">
                        {participant?.name}
                      </h4>
                      {participant?.isHost && !(currentUserId && String(participant?.id) === String(currentUserId)) && (
                        <span className="text-xs bg-accent text-accent-foreground px-2 py-0.5 rounded-full">
                          Anfitrión
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-text-secondary">
                      {getStatusLabel(participant?.status)}
                      {participant?.joinedAt && (
                        <span className="ml-2">
                          • Unido {new Date(participant.joinedAt)?.toLocaleTimeString('es-ES', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      )}
                    </p>
                  </div>
                </div>

                {!participant?.isHost && (isCurrentUserHost || participant?.status === 'pending' || String(participant?.id) === String(currentUserId)) && (
                  <Button
                    variant={String(participant?.id) === String(currentUserId) ? 'ghost' : 'outline'}
                    size="sm"
                    onClick={() => onRemoveParticipant && onRemoveParticipant(participant?.id)}
                    iconName={String(participant?.id) === String(currentUserId) ? 'LogOut' : 'X'}
                    iconSize={14}
                    className={String(participant?.id) === String(currentUserId) ? 'text-text-secondary hover:text-foreground' : 'text-error hover:text-error hover:bg-error/10'}
                    title={String(participant?.id) === String(currentUserId) ? 'Salir de la sesión' : 'Eliminar comensal'}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Action Section */}
      <div className="p-4 bg-muted/30 border-t border-border">
        <div className="space-y-3">
          {/* Status Message */}
          <div className="text-center">
            {canStartSplitting ? (
              <p className="text-sm text-success">
                ✓ Listos para comenzar la división
              </p>
            ) : (
              <p className="text-sm text-text-secondary">
                Esperando más comensales para comenzar
              </p>
            )}
          </div>

          {/* Start Button */}
          <Button
            variant="default"
            fullWidth
            disabled={!canStartSplitting}
            onClick={onStartSplitting}
            iconName="ArrowRight"
            iconPosition="right"
            iconSize={16}
          >
            Comenzar División
          </Button>

          {/* Session Info */}
          <div className="text-center text-xs text-text-secondary">
            Sesión ID: {sessionId} • Expira en 45 min
          </div>
        </div>
      </div>
    </div>
  );
};

export default ParticipantManagementPanel;